import { MapColorThemeContextProps, MapStyle } from "@app/shared/map-appearance/interfaces";
import { MapAppearancePanel } from "@app/shared/map-appearance/components/MapAppearancePanel";
import { MapCustomControlProps } from "@app/shared/mapping/MapCustomControlProps";
import { useMapboxControl } from "@app/shared/mapping/useMapBoxControl";
import { QESButton } from "@app/shared/qes-standards/Button";
import { Context, FC, useState } from "react";
import { createPortal } from "react-dom";

interface MapAppearanceControlProps extends MapCustomControlProps {
  /** The context defining color themes returned by `createMapColorThemeContext()`. */
  colorThemeContext: Context<MapColorThemeContextProps | null>;
  /** Title of the flyout used to edit color themes. */
  colorThemeFlyoutTitle?: string;
  /** Label for color theme select element. */
  colorThemeSelectLabel?: string;
  /** ClassName to add on the outside container of the component. */
  containerClassName?: string;
  /** Called when user selects a different style in `styleOptions`. */
  onStyleChange?: (newStyleUrl: string) => void;
  /** Position to place the control within the map. */
  position: NonNullable<MapCustomControlProps['position']>;
  /** The current selected style's url. Determines which radio button is checked. */
  selectedStyle?: string;
  /** The style options the user can choose from. Map style options are hidden if this is undefined or empty. */
  styleOptions?: MapStyle[];
  /** Prefix used for various data-testids. */
  testId: string;
}

export const MapAppearanceControl: FC<MapAppearanceControlProps> = ({
  colorThemeContext,
  colorThemeFlyoutTitle,
  colorThemeSelectLabel,
  containerClassName,
  ignoreInMapExport = true,
  mapRef,
  onStyleChange,
  position,
  selectedStyle,
  styleOptions,
  testId,
}) => {
  const [showControl, setShowControl] = useState(false);
  const container = useMapboxControl({ mapRef, position });

  const imagesDir = `${window.customUIBaseUrl}/images/`;
  const alignClass = position.includes('left')
    ? 'map-appearance__map-control-container--left'
    : 'map-appearance__map-control-container--right';

  if (!container) return null;

  return createPortal(
    <div {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>
      <div className={`map-appearance__map-control-container ${alignClass} ${containerClassName ?? ""}`} >
        <QESButton
          aria-label="Map Appearance Controls"
          aria-expanded={showControl}
          className="map-appearance__map-control-toggle"
          onClick={() => setShowControl(prev => !prev)}
          qesTestId={`${testId}__map-appearance__map-control-toggle`}
          qesType="none"
        >
          <img
            alt=""
            className="map-appearance__map-control-icon"
            src={`${imagesDir}Layer_Hover.png`}
          />
        </QESButton>
        {showControl && (
          <MapAppearancePanel
            colorThemeContext={colorThemeContext}
            colorThemeFlyoutTitle={colorThemeFlyoutTitle}
            colorThemeSelectLabel={colorThemeSelectLabel}
            onStyleChange={onStyleChange}
            selectedStyle={selectedStyle}
            styleOptions={styleOptions}
            testId={testId}
          />
        )}
      </div>
    </div>,
    container
  );
};
